import type { Customer, EntryPoint, Nullable } from 'corgi-types';

import type { IconName } from 'common/components/Icon';
import { HOTJAR_SUPPRESS_ATTRS } from 'common/constants/hotjar';
import type { LocaleProps } from 'common/providers/LocaleProvider';
import type { GraphqlNavigationItemProps } from 'common/types/NavigationItem';
import { SideNavType } from 'common/utils/navigation';
import type { CurrentNavigation } from './types';

export type NavigationIconData = { type: 'img'; src: string } | { type: 'icon'; name: IconName };

interface NavigationItem {
  label: string;
  href: string;
  icon?: NavigationIconData;
  onClick?: () => void;
}

interface GetNavigationItemsParams extends LocaleProps {
  customer: Nullable<Customer>;
}

const CASCADING_DRAWER_WIDTH = [
  // Width for L, XL
  [344, 416], // 1 level
  [664, 784], // 2 levels
  [984, 1152], // 3 levels
];
const MOBILE_DRAWER_WIDTH = 320;

export const getUspEntryPoints = (uspEntryPoints: EntryPoint[]): NavigationItem[] =>
  uspEntryPoints
    .map(
      ({ icon, label, labelUrl: { fullUrl: href } }) =>
        !!label &&
        !!href && {
          label,
          href,
          icon: { type: 'img' as const, src: icon.url },
        }
    )
    .filter(Boolean);

export const getNavigationItems = ({
  l10n,
  customer,
}: GetNavigationItemsParams): NavigationItem[] => {
  const { t, p, r } = l10n;

  let navigation: NavigationItem[] = [
    customer?.isLoggedIn
      ? {
          label: r(t('h24_not_your_account'), {
            username: customer.firstName,
          }),
          href: p.t('h24_logout_url'),
          ...HOTJAR_SUPPRESS_ATTRS,
          icon: { type: 'icon', name: 'logout' },
        }
      : {
          label: t('h24_register'),
          href: p.t('h24_login_page_url'),
          icon: { type: 'icon', name: 'login' },
        },
  ];

  navigation = [
    ...navigation,
    {
      label: t('h24_my_account'),
      href: p.t('h24_my_overview_url'),
      icon: { type: 'icon', name: 'profile' },
    },
  ];

  return navigation;
};

interface GetCascadingNavigationDataParams {
  currentNavigation: CurrentNavigation;
  fetchedNavigation: GraphqlNavigationItemProps;
  selectedItem: Nullable<number>;
}

export const getCascadingNavigationData = ({
  currentNavigation,
  fetchedNavigation,
  selectedItem,
}: GetCascadingNavigationDataParams): GraphqlNavigationItemProps[] => {
  if (!fetchedNavigation.children?.length) {
    return [fetchedNavigation];
  }

  if (selectedItem !== null) {
    return [
      fetchedNavigation.children[selectedItem],
      ...currentNavigation.map(item => fetchedNavigation.children[selectedItem].children[item]),
    ];
  }

  return [
    fetchedNavigation,
    ...currentNavigation.map((item, level, map) =>
      level === 0
        ? fetchedNavigation.children[item]
        : fetchedNavigation.children[map[level - 1]].children[item]
    ),
  ];
};

export const getNavigationData = (
  currentNavigation: CurrentNavigation,
  fetchedNavigation: GraphqlNavigationItemProps
) => {
  if (!fetchedNavigation.children?.length) {
    return fetchedNavigation;
  }

  return currentNavigation.reduce((data, next) => data.children[next], fetchedNavigation);
};

const sortByPrimary = (items: GraphqlNavigationItemProps[] = []) => {
  if (items.length <= 2) {
    return items;
  }

  const [first, ...otherItems] = items;

  return [
    first,
    ...otherItems.sort((a, b) => {
      if (a.isPrimary && !b.isPrimary) {
        return -1; // "a" comes before "b"
      } else if (!a.isPrimary && b.isPrimary) {
        return 1; // "b" comes before "a"
      } else {
        return 0; // no sorting
      }
    }),
  ];
};

const filterEmptyPromoImgV2 = (item: GraphqlNavigationItemProps) => {
  if ('promoImgV2' in item) {
    const { x1, ctaText } = item.promoImgV2 || {};

    // Remove empty promoImgV2 items from the list
    return item.promoImgV2 && x1 && ctaText;
  }

  return true;
};

export const sortNavigation = (
  items: Nullable<GraphqlNavigationItemProps[]> = [],
  type = SideNavType.Global
): GraphqlNavigationItemProps[] => {
  if (type === SideNavType.ShopInShop) {
    return items;
  }

  // sort excluding first item
  const sortedList = sortByPrimary(items)
    .filter(filterEmptyPromoImgV2)
    .map(item => ({
      ...item,
      children: sortNavigation(item.children),
    }));

  // ignore if the list has only the promoImg
  if (sortedList.length === 1 && 'promoImgV2' in sortedList[0]) {
    return null;
  }

  return sortedList;
};

export const getSideNavDrawerContainerStyles = (level: number, navLength: number) => ({
  ...(level === 0 && { pl: [null, null, 'sp_32', 'sp_48'] }),
  ...(level === navLength - 1 && {
    pr: [null, null, 'sp_32', 'sp_48'],
  }),
  ...(navLength === 3 && {
    // 3 columns on L (decrease width)
    minWidth: [null, null, 280, 320],
  }),
});

export const geSideNavDrawerWrapperStyles = (level: number, selectedItem: Nullable<number>) => ({
  ...((level !== 0 || selectedItem != null) && {
    pb: ['sp_16', null, 'sp_20', 'sp_24'],
  }),
});

export const sanitizeTabLabel = (label: string) => label.toLowerCase().replace(/\s+/g, '-');

export const getSideDrawerWidth = (isOpen: boolean, isCascading: boolean, currentLevel: number) => {
  if (isOpen && isCascading) {
    return [null, null, ...CASCADING_DRAWER_WIDTH[currentLevel]];
  }

  return MOBILE_DRAWER_WIDTH;
};
